import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Wallet, ArrowUpRight, ArrowDownRight, History, CreditCard, 
  Plus, RefreshCw, TrendingUp, Shield, AlertCircle,
  ArrowLeft, Home, Search, Bell, User, Filter, Download,
  Calendar, X, ChevronLeft, ChevronRight, Smartphone, Monitor,
  Clock, FileText, CheckCircle, XCircle, Loader, Copy, ExternalLink,
  Receipt, Banknote, Tag, Building, Globe, Smartphone as SmartphoneIcon,
  CreditCard as CreditCardIcon, Wallet as WalletIcon, Check,
  Key, Shield as ShieldIcon, AlertTriangle, Info,
  PlusCircle, LogIn, MessageSquare, Mail, HelpCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { 
  getUserSupportTickets, 
  getSupportTicketsInvolved 
} from '../../services/api';
import Loading from '../../components/common/Loading';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import SupportTabs from '../../components/support/Support/SupportTabs';
import SupportTicketItem from '../../components/support/Support/SupportTicketItem';
import SupportEmptyState from '../../components/support/Support/SupportEmptyState';
import ConfirmationModal from '../../components/common/ConfirmationModal';

const SupportHomeScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [activeTab, setActiveTab] = useState('all');
  const [myTickets, setMyTickets] = useState([]);
  const [involvedTickets, setInvolvedTickets] = useState([]);
  const [allTickets, setAllTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Modal states
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  const colors = {
    primary: '#ff660e',
    black: '#0f0e0e',
    white: '#ffffff',
    lightBg: '#f8f9fa'
  };

  useEffect(() => {
    if (user) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchData = async (isRefreshing = false) => {
    try {
      if (!isRefreshing) setLoading(true);
      
      const [myTicketsResponse, involvedTicketsResponse] = await Promise.all([
        getUserSupportTickets(user.user_id),
        getSupportTicketsInvolved(user.user_id)
      ]);
      
      let myTicketsData = [];
      let involvedTicketsData = [];
      
      if (myTicketsResponse.success) {
        myTicketsData = (myTicketsResponse.data || []).map(ticket => ({
          ...ticket,
          is_owner: true,
          is_involved: false,
          ticket_type: 'my_ticket'
        }));
      }
      
      if (involvedTicketsResponse.success) {
        involvedTicketsData = (involvedTicketsResponse.data || []).map(ticket => ({
          ...ticket,
          is_owner: ticket.user_id === user.user_id,
          is_involved: true,
          ticket_type: 'dispute_ticket'
        }));
      }
      
      setMyTickets(myTicketsData);
      setInvolvedTickets(involvedTicketsData);
      
      const combinedTickets = [...myTicketsData, ...involvedTicketsData];
      const uniqueTickets = Array.from(
        new Map(combinedTickets.map(ticket => [ticket.ticket_id, ticket])).values()
      );
      
      setAllTickets(uniqueTickets);
      filterTicketsByTab(uniqueTickets, activeTab);
      
    } catch (error) {
      // console.error('Error fetching support data:', error);
      alert('Failed to load support data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const filterTicketsByTab = (tickets, tab) => {
    let filtered = tickets;
    
    switch(tab) {
      case 'my':
        filtered = tickets.filter(t => t.is_owner);
        break;
      case 'involved':
        filtered = tickets.filter(t => t.is_involved && t.dispute_id);
        break;
      case 'open':
        filtered = tickets.filter(t => t.status === 'open' || t.status === 'awaiting_user');
        break;
      case 'in_progress':
        filtered = tickets.filter(t => t.status === 'in_progress');
        break;
      case 'resolved':
        filtered = tickets.filter(t => t.status === 'resolved');
        break;
      default:
        filtered = tickets;
    }
    
    setFilteredTickets(filtered);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    filterTicketsByTab(allTickets, tab);
  };

  const onRefresh = async () => {
    if (!user) return;
    setRefreshing(true);
    await fetchData(true);
  };

  const handleCreateTicket = () => {
    if (user) {
      navigate('/support/new-ticket');
    } else {
      // Show modal instead of confirm()
      setShowLoginModal(true);
    }
  };
  
  const handleLoginConfirm = () => {
    navigate('/login');
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@ecomart.ng';
  };


  // Calculate tab counts
  const tabCounts = {
    my: myTickets.length,
    involved: involvedTickets.filter(t => t.dispute_id).length,
    open: allTickets.filter(t => t.status === 'open' || t.status === 'awaiting_user').length,
    in_progress: allTickets.filter(t => t.status === 'in_progress').length,
    resolved: allTickets.filter(t => t.status === 'resolved').length,
    all: allTickets.length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading text="Loading support tickets..." />
      </div>
    );
  }

  // ========== MOBILE VIEW ==========
  const MobileView = () => (
    <div className="lg:hidden min-h-screen bg-gray-50 pb-20">
      {/* Mobile Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5 text-black" />
            </button>
            
            <h1 className="text-lg font-bold text-black">Support Center</h1>
            
            <button
              onClick={handleCreateTicket}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <PlusCircle className="w-5 h-5 text-[#ff660e]" />
            </button>
          </div>
        </div>
      </header>

      <main className="px-4 py-4">
        {/* Not Logged In State - Mobile */}
        {!user && (
          <div className="bg-white rounded-xl shadow-sm p-5 mb-4">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <LogIn className="w-8 h-8 text-gray-400" />
              </div>
              
              <h2 className="text-lg font-bold text-black mb-2">Login Required</h2>
              <p className="text-sm text-gray-600 mb-6">
                Please login to view your support tickets and create new ones.
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full py-3 bg-[#ff660e] text-white rounded-lg font-medium hover:bg-[#ff5500]"
                >
                  Login to Continue
                </button>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">Need immediate assistance?</p>
                  <a 
                    href="mailto:support@ecomart.ng"
                    className="text-[#ff660e] font-medium hover:underline text-sm"
                  >
                    support@ecomart.ng
                  </a>
                </div>
                
                <button
                  onClick={() => setShowContactModal(true)}
                  className="w-full py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 flex items-center justify-center gap-2"
                >
                  <MessageSquare className="w-4 h-4" />
                  Contact Support
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Logged In Content - Mobile */}
        {user && (
          <>
            {/* Tabs - Mobile */}
            <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
              <SupportTabs 
                activeTab={activeTab}
                setActiveTab={handleTabChange}
                counts={tabCounts}
                mobile={true}
              />
            </div>

            {/* Refresh Button - Mobile */}
            <div className="mb-4">
              <button
                onClick={onRefresh}
                disabled={refreshing}
                className="flex items-center justify-center gap-2 w-full py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh Tickets'}</span>
              </button>
            </div>

            {/* Tickets List - Mobile */}
            <div className="space-y-3">
              {filteredTickets.length === 0 ? (
                <SupportEmptyState activeTab={activeTab} />
              ) : (
                filteredTickets.map((ticket) => (
                  <SupportTicketItem key={ticket.ticket_id} item={ticket} mobile={true} />
                ))
              )}
            </div>
          </>
        )}
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );

  // ========== DESKTOP VIEW ==========
  const DesktopView = () => (
    <div className="hidden lg:block min-h-screen bg-gray-50">
      {/* Desktop Header */}
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-black">Support Center</h1>
            <p className="text-gray-600">Manage and track your support tickets</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <button
              onClick={handleCreateTicket}
              className="flex items-center gap-2 px-4 py-2 bg-[#ff660e] text-white rounded-lg hover:bg-[#ff5500]"
            >
              <PlusCircle className="w-5 h-5" />
              Create Ticket
            </button>
          </div>
        </div>

        {/* Not Logged In State - Desktop */}
        {!user && (
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-10 text-center">
              <div className="w-24 h-24 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-8">
                <LogIn className="w-12 h-12 text-gray-400" />
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Login Required</h2>
              <p className="text-gray-600 mb-8 text-lg">
                Please login to view your support tickets and create new ones.
              </p>
              
              <div className="max-w-md mx-auto space-y-4">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full bg-[#ff660e] text-white py-3.5 rounded-xl font-semibold hover:bg-[#ff5500]"
                >
                  Login to Continue
                </button>
                
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Need immediate assistance?</h3>
                  <p className="text-gray-600 mb-3">Contact us directly at:</p>
                  <a 
                    href="mailto:support@ecomart.ng"
                    className="text-[#ff660e] font-semibold hover:underline text-lg"
                  >
                    support@ecomart.ng
                  </a>
                </div>
                
                <button
                  onClick={() => setShowContactModal(true)}
                  className="w-full border-2 border-[#ff660e] text-[#ff660e] py-3.5 rounded-xl font-semibold hover:bg-orange-50 transition flex items-center justify-center"
                >
                  <MessageSquare className="w-5 h-5 mr-2" />
                  Create Support Ticket
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Logged In Content - Desktop */}
        {user && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left Column - Tabs & Stats */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-black mb-6">Ticket Types</h3>
                <SupportTabs 
                  activeTab={activeTab}
                  setActiveTab={handleTabChange}
                  counts={tabCounts}
                  desktop={true}
                />
                
                {/* Stats Summary */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h4 className="font-semibold text-black mb-4">Ticket Statistics</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Open Tickets</span>
                      <span className="font-semibold text-yellow-600">{tabCounts.open}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">In Progress</span>
                      <span className="font-semibold text-blue-600">{tabCounts.in_progress}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Resolved</span>
                      <span className="font-semibold text-green-600">{tabCounts.resolved}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total Tickets</span>
                      <span className="font-semibold text-black">{tabCounts.all}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Tickets List */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-black">
                      {activeTab === 'my' ? 'My Support Tickets' :
                       activeTab === 'involved' ? 'Disputes I\'m Involved In' :
                       activeTab === 'open' ? 'Open Tickets' :
                       activeTab === 'in_progress' ? 'In Progress Tickets' :
                       activeTab === 'resolved' ? 'Resolved Tickets' :
                       'All Support Tickets'}
                    </h3>
                    <span className="text-sm text-gray-600">
                      {filteredTickets.length} {filteredTickets.length === 1 ? 'ticket' : 'tickets'}
                    </span>
                  </div>
                </div>
                
                {/* Tickets List */}
                <div className="divide-y divide-gray-200">
                  {filteredTickets.length === 0 ? (
                    <div className="p-12 text-center">
                      <SupportEmptyState activeTab={activeTab} />
                    </div>
                  ) : (
                    filteredTickets.map((ticket) => (
                      <SupportTicketItem key={ticket.ticket_id} item={ticket} desktop={true} />
                    ))
                  )}
                </div>
              </div>
              
              {/* Help Section */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <HelpCircle className="w-6 h-6 text-blue-600" />
                    <h4 className="font-semibold text-black">Need Help?</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Can't find what you're looking for? Our support team is here to help.
                  </p>
                  <button
                    onClick={() => navigate('/support/new-ticket')}
                    className="w-full py-2.5 text-sm bg-[#ff660e] text-white rounded-lg font-medium hover:bg-[#ff5500]"
                  >
                    Create New Ticket
                  </button>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-100 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Mail className="w-6 h-6 text-green-600" />
                    <h4 className="font-semibold text-black">Contact Support</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    For urgent issues, contact us directly via email.
                  </p>
                  <a
                    href="mailto:support@ecomart.ng"
                    className="block w-full py-2.5 text-sm border border-green-300 text-green-700 rounded-lg font-medium hover:bg-green-50 text-center"
                  >
                    Email Support
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      
      {/* Desktop Footer */}
      <Footer />
    </div>
  );

  return (
    <>
      {/* Render Mobile or Desktop View */}
      <MobileView />
      <DesktopView />
      
      {/* Modals */}
      <ConfirmationModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onConfirm={handleLoginConfirm}
        title="Login Required"
        message="Please login to create a support ticket"
        confirmText="Login"
        cancelText="Cancel"
      />
      
      <ConfirmationModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        onConfirm={handleContactSupport}
        title="Contact Support"
        message="Please login to create a support ticket or contact us at support@ecomart.ng"
        confirmText="Contact Support"
        cancelText="Cancel"
      />
    </>
  );
};

export default SupportHomeScreen;